import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { SheetBody, SheetFooter } from "../ui/SheetHost";

/* CashOutSheet (design 4c) — the cash-out confirm sheet (medium detent).
   The amount is the hero; stake + full payout sit beneath; "Confirm cash out" is
   the primary and "Keep bet" is a quiet escape. The live cash-out value is
   re-polled every 2s while the sheet is open — if it MOVES before confirm, the
   hero updates and a "value changed" note appears so the user re-confirms the new
   amount (never a stale number).

   REAL money-path: GET /bets|parlays/:id/cashout for the live quote, POST
   /bets|parlays/:id/cashout to settle (idempotent server-side). `bet` is the
   unified card from MobileMyBets: { id, kind, endpoint, currency, stake,
   potentialPayout, cashQuote }. onDone() refreshes the list + balances. */

const fmtNum = (amt, currency) => currency === "cash"
  ? `$${(Number(amt) || 0).toFixed(2)}`
  : (Number(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashOutSheet({ close, bet, onDone }) {
  const { id, kind, endpoint, currency, stake, potentialPayout } = bet;
  const ep = endpoint || (kind === "parlay" ? "parlays" : "bets");
  const [value, setValue] = useState(bet.cashQuote?.value ?? null);
  const [available, setAvailable] = useState(bet.cashQuote?.available !== false);
  const [moved, setMoved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const lastValue = useRef(bet.cashQuote?.value ?? null);
  const cash = currency === "cash";

  // Re-poll the live quote while the sheet is open. A moved value re-arms confirmation.
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const { data } = await api.get(`/${ep}/${id}/cashout`);
        if (!alive) return;
        setAvailable(data.available !== false);
        if (data.value != null) {
          if (lastValue.current != null && Math.abs(Number(data.value) - Number(lastValue.current)) > 1e-9) setMoved(true);
          lastValue.current = data.value;
          setValue(data.value);
        }
      } catch { /* keep last-good; the POST stays authoritative */ }
    };
    pull();
    const t = setInterval(pull, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [ep, id]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/${ep}/${id}/cashout`);
      toast.success(`Cashed out for ${fmtNum(data.value, currency)}`);
      await onDone?.();
      close && close();
    } catch (e) {
      const d = e.response?.data?.detail;
      // The offer may have changed between poll and POST — surface it and let the user re-confirm.
      if (e.response?.status === 409 && (d?.value != null || d?.current_value != null)) {
        const nv = d.value ?? d.current_value;
        lastValue.current = nv; setValue(nv); setMoved(true);
        toast.message(d.message || "The cash-out value changed — confirm the new amount.");
      } else {
        toast.error(apiError(d) || d?.message || "Cash out failed");
      }
    } finally { setConfirming(false); }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <SheetBody style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <p style={{ font: "600 14px 'Outfit'", color: "#a1a1aa", marginTop: 8 }}>
          Cash out {kind === "parlay" ? `${(bet.legs || []).length}-leg parlay` : "bet"}
        </p>

        {/* hero amount */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <Icon name={cash ? "pnrcash" : "pnrcoin"} size={30} style={{ color: cash ? "#E4E4E7" : "#00D632" }} />
          <span className="m-tnum" style={{ font: "800 44px 'Manrope'", letterSpacing: "-.04em", color: "#fff" }}>
            {value != null ? fmtNum(value, currency) : "—"}
          </span>
        </span>
        {moved && <span style={{ marginTop: 6, font: "600 12px 'Outfit'", color: "#00D632" }}>Value updated — confirm the new amount</span>}
        {!available && <span style={{ marginTop: 6, font: "600 12px 'Outfit'", color: "#EF4444" }}>Cash out unavailable · market suspended</span>}

        {/* stake / full payout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", width: "100%", marginTop: 22, borderRadius: 16, overflow: "hidden", background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          <div style={{ padding: "14px 16px", textAlign: "left" }}>
            <span style={{ display: "block", font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>Stake</span>
            <span className="m-tnum" style={{ font: "800 16px 'Manrope'", color: "#fff" }}>{fmtNum(stake, currency)}</span>
          </div>
          <div style={{ padding: "14px 16px", textAlign: "left", boxShadow: "inset 1px 0 0 #1F1F23" }}>
            <span style={{ display: "block", font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>Full payout</span>
            <span className="m-tnum" style={{ font: "800 16px 'Manrope'", color: "#fff" }}>{fmtNum(potentialPayout, currency)}</span>
          </div>
        </div>

        <p style={{ marginTop: 16, font: "500 12.5px/1.5 'Outfit'", color: "#71717a", maxWidth: 300 }}>
          Cash-out value follows live prices. If it changes before you confirm, we’ll show the new amount.
        </p>
      </SheetBody>

      <SheetFooter style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button type="button" onClick={confirm} disabled={confirming || !available || value == null}
          style={{ width: "100%", height: 56, borderRadius: 16, border: 0, cursor: (confirming || !available) ? "default" : "pointer", font: "800 16px 'Manrope'",
            background: (confirming || !available || value == null) ? "#141417" : "#F4F4F5", color: (confirming || !available || value == null) ? "#52525b" : "#0A0A0A" }}>
          {confirming ? "Cashing out…" : moved ? `Confirm new amount · ${fmtNum(value, currency)}` : "Confirm cash out"}
        </button>
        <button type="button" onClick={close} style={{ width: "100%", height: 32, background: "none", border: 0, cursor: "pointer", font: "600 15px 'Outfit'", color: "#a1a1aa" }}>Keep bet</button>
      </SheetFooter>
    </div>
  );
}
